import React, { FC, useEffect, useRef, useState } from 'react';
import { Animated, Easing, ScrollView, StyleSheet, TextProps, View } from 'react-native';

import { Theme } from '@constants';

import { styles } from './styles';

import { isWeb } from '../../../helpers/Platform';
import { variants } from '../TWCText/styles';
import { TWCText } from '../TWCText/TWCText';

export const TWCTextTicker: FC<TWCTextTickerProps> = ({
  accessibilityHint,
  accessibilityLabel,
  scrollSpacer = Theme.px(20),
  scrollSpeed = 100, // In pixels per second
  canScroll = true,
  onAnimationEnd = () => {},
  animationDelay = 1000,
  loops = false,
  children,
  nativeID,
  style,
  testID,
  variant,
  ...properties
}) => {
  const crawlOffset = useRef(new Animated.Value(scrollSpacer)).current;
  const [textWidth, setTextWidth] = useState(0);

  const startCrawl = (width: number) => {
    const totalOffset = width + scrollSpacer;
    const milliInSecond = 1000;

    Animated.timing(crawlOffset, {
      toValue: -totalOffset,
      easing: Easing.linear,
      duration: (totalOffset / Theme.px(scrollSpeed)) * milliInSecond,
      useNativeDriver: true,
    }).start(({ finished }) => {
      finished && onAnimationEnd?.();
      if (loops && finished) {
        resetText();
        if (canScroll) {
          setTimeout(() => {
            startCrawl(textWidth);
          }, animationDelay);
        }
      }
    });
  };

  const resetText = () => {
    crawlOffset.stopAnimation();
    crawlOffset.setValue(scrollSpacer);
  };

  useEffect(() => {
    resetText();
    if (canScroll) {
      setTimeout(() => {
        startCrawl(textWidth);
      }, animationDelay);
    }
    return () => {
      resetText();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textWidth, canScroll]);

  const { height } = StyleSheet.flatten(style);
  const lineHeight = Number.isInteger(height) ? Number(height) : undefined;

  return (
    <View style={[styles.container, style]}>
      <ScrollView
        horizontal
        scrollEnabled={false}
        showsHorizontalScrollIndicator={false}
        style={StyleSheet.absoluteFillObject}
      >
        <Animated.Text
          {...(!isWeb && {
            accessibilityHint,
            accessibilityLabel: accessibilityLabel || children?.toString(),
          })}
          nativeID={nativeID || testID}
          numberOfLines={1}
          style={[
            variant ? TWCText.allVariants[variant] : undefined,
            styles.text,
            { transform: [{ translateX: crawlOffset }], lineHeight },
          ]}
          testID={testID}
          onLayout={layout => {
            if (textWidth !== layout.nativeEvent.layout.width) {
              setTextWidth(layout.nativeEvent.layout.width);
            }
          }}
          {...properties}
        >
          {children}
        </Animated.Text>
      </ScrollView>
    </View>
  );
};

type TWCTextTickerProps = TextProps & {
  scrollSpacer?: number;
  scrollSpeed?: number;
  canScroll?: boolean;
  onAnimationEnd?: () => void;
  animationDelay?: number;
  loops?: boolean;
  noTestID?: boolean;
  variant?: keyof typeof variants;
};
